import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { ListenButton } from "@/components/ListenButton";

export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`transition-[opacity,transform] duration-700 ease-out ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}>
      {children}
    </div>
  );
}

export const FluidBackdrop = ({ className = "" }) => (
  <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden>
    <div className="blob blob-a" /><div className="blob blob-b" /><div className="blob blob-c" />
  </div>
);

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "bot", text: "Namaste! I'm Niro, Nirogyam's AI Agent. Ask me about booking, reports, follow-ups, home delivery or lab collection." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs, open]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    const history = msgs.slice(1).map((m) => ({ role: m.role === "user" ? "user" : "assistant", text: m.text }));
    setMsgs((m) => [...m, { role: "user", text }]);
    setText(""); setBusy(true);
    try { const { data } = await api.post("/assistant", { message: text, history }); setMsgs((m) => [...m, { role: "bot", text: data.reply, speak: true }]); }
    catch (er) { setMsgs((m) => [...m, { role: "bot", text: errMsg(er) }]); } finally { setBusy(false); }
  };
  return (
    <div className="fixed z-50 right-4 bottom-20 lg:bottom-6 no-print">
      {open && (
        <div className="mb-3 w-[min(92vw,360px)] h-[460px] surface shadow-[0_30px_80px_-30px_rgba(36,18,181,0.45)] flex flex-col overflow-hidden chat-pop" data-testid="chatbot-panel">
          <div className="px-4 py-3 bg-[#2412B5] text-white flex items-center justify-between">
            <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4" />Niro <span className="font-normal opacity-80">· Nirogyam's AI Agent</span></p>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chatbot-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-[#086B2D] text-white" : "bg-[#F7F9FA] border border-[#E2E8ED]"}`}>{m.text}
                {m.role === "bot" && m.speak && <div className="mt-1 flex justify-end"><ListenButton text={m.text} compact testId={`chatbot-listen-${i}`} /></div>}</div>
            ))}
            {busy && <Loader2 className="h-4 w-4 animate-spin text-[#2412B5]" />}
            <div ref={end} />
          </div>
          <p className="px-3 text-[10px] muted">AI help for using the app – not medical advice. Emergency? Call 112.</p>
          <form onSubmit={send} className="p-3 flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a question" data-testid="chatbot-input" />
            <Button size="icon" type="submit" disabled={busy} data-testid="chatbot-send" aria-label="Send"><Send className="h-4 w-4" /></Button></form>
        </div>
      )}
      <button onClick={() => setOpen(!open)} data-testid="chatbot-toggle" aria-label="Open Niro, Nirogyam's AI Agent"
        className="ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2412B5] text-white shadow-lg hover:scale-105 transition-transform">
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-6 w-6" />}
        {!open && <span className="absolute inset-0 rounded-full border-2 border-[#2412B5]/40 pulse-ring" />}
      </button>
    </div>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(() => !localStorage.getItem("nt_cookie_ack"));
  if (!show) return null;
  return (
    <div className="fixed bottom-24 left-3 right-20 sm:right-auto lg:bottom-6 lg:left-72 z-40 no-print sm:max-w-sm" data-testid="cookie-banner">
      <div className="surface p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-xl fade-up">
        <p className="text-sm muted flex-1">We use only essential cookies to keep you securely signed in. No advertising or tracking cookies. <Link to="/cookies" className="text-[#2412B5]">Cookie statement</Link></p>
        <Button size="sm" className="rounded-full" onClick={() => { localStorage.setItem("nt_cookie_ack", "1"); setShow(false); }} data-testid="cookie-accept-btn">Got it</Button>
      </div>
    </div>
  );
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export function RouteFade({ children }) {
  const loc = useLocation();
  return <div key={loc.pathname} className="page-enter">{children}</div>;
}

export function Floating() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/shared/") || pathname.startsWith("/devops") || pathname.startsWith("/q-log")) return null;
  return <><ChatWidget /><CookieBanner /></>;
}
